import { z } from 'zod'
import { EntityIdSchema, idSchemaV2, isId, WorkflowIdSchema } from '../ids'
import { CustomFieldFormatSchema } from '../customField'
import { AuditPropsSchema } from '../common'

export const RatioIdSchema = idSchemaV2('Ratio')
export type RatioId = z.infer<typeof RatioIdSchema>
export const isRatioId = isId(RatioIdSchema)

export const RatioTypeSchema = z.enum(['ratio', 'percentage', 'formula'])
export type RatioType = z.infer<typeof RatioTypeSchema>

export const ReportFormattersSchema = z.union([
  z.enum(['round', 'ceiling', 'floor', 'daysSince', 'timeOfDay']),
  RatioTypeSchema,
  CustomFieldFormatSchema,
])

export type ReportFormatters = z.infer<typeof ReportFormattersSchema>

export const ReportAccumulatorSchema = z.enum(['sum', 'min', 'max', 'avg', 'median', 'distinct', 'query'])
export type ReportAccumulator = z.infer<typeof ReportAccumulatorSchema>

const UnsavedRatioDataBaseSchema = z.object({
  name: z.string().nonempty(),
  type: RatioTypeSchema.extract(['ratio', 'percentage']),
  description: z.string().optional(),
  short: z.string().optional(),
  plural: z.string().optional(),
  scopeId: EntityIdSchema.optional(),
  workflowId: WorkflowIdSchema.optional(),
})

const FormulaScopeSchema = z.record(z.templateLiteral([z.literal('$'), z.string()]), z.string())
export type FormulaScope = z.infer<typeof FormulaScopeSchema>

export const UnsavedFormulaRatioDataSchema = z.object({
  ...UnsavedRatioDataBaseSchema.shape,
  type: RatioTypeSchema.extract(['formula']),
  formula: z.string().nonempty(),
  scopes: FormulaScopeSchema,
  formatter: ReportFormattersSchema.optional(),
  accumulator: ReportAccumulatorSchema.optional(),
})
export type UnsavedFormulaRatioData = z.infer<typeof UnsavedFormulaRatioDataSchema>

export const UnsavedFractionalRatioDataSchema = z.object({
  ...UnsavedRatioDataBaseSchema.shape,
  numerator: z.string().nonempty(),
  denominator: z.string().nonempty(),
})
export type UnsavedFractionalRatioData = z.infer<typeof UnsavedFractionalRatioDataSchema>

export const UnsavedRatioDataSchema = z.discriminatedUnion('type', [
  UnsavedFormulaRatioDataSchema,
  UnsavedFractionalRatioDataSchema,
])
export type UnsavedRatioData = z.infer<typeof UnsavedRatioDataSchema>

export const RatioDataSchema = z
  .object({
    ...AuditPropsSchema.shape,
    ratioId: RatioIdSchema,
    scopeId: EntityIdSchema,
  })
  .and(UnsavedRatioDataSchema)

export type RatioData = z.infer<typeof RatioDataSchema>

export type FractionalRatioData = Extract<RatioData, { type: 'ratio' | 'percentage' }>
export const isFractionalRatioData = (ratio: RatioData): ratio is FractionalRatioData =>
  ratio.type === 'ratio' || ratio.type === 'percentage'
export type FormulaRatioData = Extract<RatioData, { type: 'formula' }>
export const isFormulaRatioData = (ratio: RatioData): ratio is FormulaRatioData => ratio.type === 'formula'
